// ============================================================================
// RADAR SETTINGS HOOK
// ============================================================================
// Manages radar_settings table: per-rule thresholds, enabled/disabled, severity.
// Also manages radar_item_status for acknowledge/snooze/resolve actions.
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { requireSession } from '../lib/supabaseAuthHelpers';

// Default rule configurations (used as fallback when DB has no row)
export const DEFAULT_RULE_CONFIGS = {
  invoice_overdue:          { warningDays: 7, criticalDays: 30 },
  invoice_not_sent:         { daysAfterStageClose: 7 },
  cashflow_warning:         { healthyThreshold: 500000, watchThreshold: 300000, warningThreshold: 150000, criticalThreshold: 50000 },
  payment_received:         {},
  invoice_reminder:         { daysBeforeDue: 7 },
  ca_monthly_reminder:      { dayOfMonth: 10 },
  timeline_overdue:         { warningDays: 7, criticalDays: 30 },
  profitability_risk:       { marginThresholdPercent: 0 },
  new_inquiry:              { recentDays: 7 },
  inquiry_followup_overdue: { overdueDays: 0, criticalDays: 14 },
  hiring_stagnation:        { daysInFirstStage: 15, daysPostInterview: 15 },
  client_birthday:          { daysAhead: 7 },
  client_anniversary:       { daysAhead: 7 },
  time_not_tracked:         { maxUntrackedDays: 5 },
  hours_under_target:       { targetPercent: 95 },
  overwork_warning:         { maxWeeklyHours: 50, maxMonthlyOverPercent: 120 },
  user_setup_incomplete:    {},
  data_incomplete_project:  {},
  data_incomplete_client:   {},
  data_incomplete_invoice:  {},
  data_incomplete_timeline: {},
  data_incomplete_team_member: {},
  data_incomplete_salary:   {},
  invoice_proforma_to_send: {},
  invoice_tax_to_send:      {},
  data_integrity_stage_dates: {},
  press_folder_drift:       { includeUnexpected: true },
};

// Human-readable rule metadata
export const RULE_META = {
  invoice_overdue:          { label: 'Invoice Overdue',              domain: 'finance',  description: 'Invoices past their payment due date' },
  invoice_not_sent:         { label: 'Invoice Not Sent',             domain: 'finance',  description: 'Stage closed but invoice not yet issued' },
  cashflow_warning:         { label: 'Cashflow Warning',             domain: 'finance',  description: 'Cashflow below defined thresholds' },
  payment_received:         { label: 'Payment Received',             domain: 'finance',  description: 'New payment detected in bank inbox' },
  invoice_reminder:         { label: 'Invoice Send Reminder',        domain: 'finance',  description: 'Upcoming invoices approaching due date' },
  ca_monthly_reminder:      { label: 'CA Monthly Reminder',          domain: 'finance',  description: 'Send previous month invoices to Chartered Accountant' },
  timeline_overdue:         { label: 'Timeline Milestone Overdue',   domain: 'projects', description: 'Planned project stage dates exceeded' },
  profitability_risk:       { label: 'Profitability at Risk',        domain: 'projects', description: 'Cumulative income below cumulative expenses' },
  new_inquiry:              { label: 'New Inquiry Received',         domain: 'bd',       description: 'Recently received inquiries' },
  inquiry_followup_overdue: { label: 'Inquiry Follow-up Overdue',    domain: 'bd',       description: 'Inquiry follow-up date has passed' },
  hiring_stagnation:        { label: 'Hiring Stagnation',            domain: 'hiring',   description: 'Candidates stalled in pipeline stages' },
  time_not_tracked:         { label: 'Time Not Tracked',             domain: 'team',     description: 'Users with untracked working days' },
  hours_under_target:       { label: 'Hours Under Target',           domain: 'team',     description: 'Users below contracted hours threshold' },
  overwork_warning:         { label: 'Overwork Warning',             domain: 'team',     description: 'Users or teams exceeding safe work limits' },
  user_setup_incomplete:    { label: 'User Setup Incomplete',        domain: 'system',   description: 'Users missing essential profile data' },
  data_incomplete_project:  { label: 'Project Data Incomplete',      domain: 'system',   description: 'Projects missing key information' },
  data_incomplete_client:   { label: 'Client Data Incomplete',       domain: 'system',   description: 'Clients missing essential fields' },
  data_incomplete_invoice:  { label: 'Invoice Data Incomplete',      domain: 'system',   description: 'Invoices with missing required fields' },
  data_incomplete_timeline: { label: 'Timeline Incomplete',          domain: 'system',   description: 'Projects without timeline data' },
  data_incomplete_team_member: { label: 'Team Member Incomplete',    domain: 'system',   description: 'Team members missing profile data' },
  data_incomplete_salary:   { label: 'Salary Data Missing',          domain: 'system',   description: 'Missing salary information for team members' },
  invoice_proforma_to_send: { label: 'Proforma Invoice To Send',      domain: 'finance',  description: 'Proforma invoice date set — admin action needed' },
  invoice_tax_to_send:      { label: 'Tax Invoice To Send',           domain: 'finance',  description: 'Invoice date set — admin action needed to send tax invoice' },
  client_birthday:          { label: 'Client Birthday',               domain: 'bd',       description: 'Upcoming client birthday — send wishes' },
  client_anniversary:       { label: 'Client Anniversary',            domain: 'bd',       description: 'Upcoming client anniversary — send wishes' },
  data_integrity_stage_dates: { label: 'Stage Date Not Backed by Invoice', domain: 'finance', description: 'A payment, invoice or proforma date on the Tracking page that the invoice ledger does not corroborate. Refreshed nightly.' },
  press_folder_drift:       { label: 'Press Folder Changed in Dropbox',  domain: 'projects', description: 'A press-kit folder was renamed, moved or deleted in Dropbox and no longer matches the schema. Reported by the Dropbox webhook within seconds of the change.' },
};

export function useRadarSettings() {
  const [settings, setSettings] = useState({});  // { [ruleKey]: { enabled, severity, config } }
  const [itemStatuses, setItemStatuses] = useState([]); // radar_item_status rows
  const [loading, setLoading] = useState(true);

  // Fetch all radar settings
  const fetchSettings = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('radar_settings')
        .select('*')
        .order('rule_key');
      if (error) throw error;

      const map = {};
      for (const row of (data || [])) {
        map[row.rule_key] = {
          enabled: row.enabled,
          severity: row.severity,
          config: { ...(DEFAULT_RULE_CONFIGS[row.rule_key] || {}), ...(row.config || {}) },
        };
      }
      // Fill in defaults for any missing rules
      for (const key of Object.keys(DEFAULT_RULE_CONFIGS)) {
        if (!map[key]) {
          map[key] = { enabled: true, severity: RULE_META[key]?.domain === 'finance' ? 'warning' : 'warning', config: DEFAULT_RULE_CONFIGS[key] };
        }
      }
      setSettings(map);
    } catch (err) {
      console.error('Failed to fetch radar settings:', err);
      // Fall back to defaults
      const map = {};
      for (const key of Object.keys(DEFAULT_RULE_CONFIGS)) {
        map[key] = { enabled: true, severity: 'warning', config: DEFAULT_RULE_CONFIGS[key] };
      }
      setSettings(map);
    }
  }, []);

  // Fetch all item statuses (includes dismissed so they can be filtered out)
  const fetchItemStatuses = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('radar_item_status')
        .select('*');
      if (error) throw error;
      setItemStatuses(data || []);
    } catch (err) {
      console.error('Failed to fetch radar item statuses:', err);
      setItemStatuses([]);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchSettings(), fetchItemStatuses()]);
      setLoading(false);
    };
    load();
  }, [fetchSettings, fetchItemStatuses]);

  // Get effective config for a rule, merging DB settings with defaults
  const getRuleConfig = useCallback((ruleKey) => {
    return settings[ruleKey] || { enabled: true, severity: 'warning', config: DEFAULT_RULE_CONFIGS[ruleKey] || {} };
  }, [settings]);

  // Check if a rule is enabled
  const isRuleEnabled = useCallback((ruleKey) => {
    return getRuleConfig(ruleKey).enabled;
  }, [getRuleConfig]);

  // Update a rule's settings
  const updateRule = useCallback(async (ruleKey, updates) => {
    try {
      const { user } = await requireSession();
      const current = settings[ruleKey] || {};
      const payload = {
        rule_key: ruleKey,
        enabled: updates.enabled ?? current.enabled ?? true,
        severity: updates.severity ?? current.severity ?? 'warning',
        config: updates.config ?? current.config ?? DEFAULT_RULE_CONFIGS[ruleKey] ?? {},
        updated_at: new Date().toISOString(),
        updated_by: user.id,
      };
      const { error } = await supabase
        .from('radar_settings')
        .upsert(payload, { onConflict: 'rule_key' });
      if (error) throw error;

      setSettings(prev => ({
        ...prev,
        [ruleKey]: { enabled: payload.enabled, severity: payload.severity, config: payload.config },
      }));
      return { success: true };
    } catch (err) {
      console.error('Failed to update radar setting:', err);
      return { success: false, error: err.message };
    }
  }, [settings]);

  // Get item status for a specific radar item
  const getItemStatus = useCallback((ruleKey, entityType, entityId) => {
    return itemStatuses.find(s =>
      s.rule_key === ruleKey &&
      s.entity_type === entityType &&
      String(s.entity_id) === String(entityId)
    );
  }, [itemStatuses]);

  // Acknowledge a radar item
  const acknowledgeItem = useCallback(async (ruleKey, entityType, entityId) => {
    try {
      const { user } = await requireSession();
      const { error } = await supabase
        .from('radar_item_status')
        .upsert({
          rule_key: ruleKey,
          entity_type: entityType,
          entity_id: String(entityId),
          status: 'acknowledged',
          acknowledged_at: new Date().toISOString(),
          acknowledged_by: user.id,
        }, { onConflict: 'rule_key,entity_type,entity_id' });
      if (error) throw error;
      await fetchItemStatuses();
      return { success: true };
    } catch (err) {
      console.error('Failed to acknowledge radar item:', err);
      return { success: false, error: err.message };
    }
  }, [fetchItemStatuses]);

  // Snooze a radar item
  const snoozeItem = useCallback(async (ruleKey, entityType, entityId, snoozeDays = 7) => {
    try {
      const { user } = await requireSession();
      const until = new Date();
      until.setDate(until.getDate() + snoozeDays);
      const { error } = await supabase
        .from('radar_item_status')
        .upsert({
          rule_key: ruleKey,
          entity_type: entityType,
          entity_id: String(entityId),
          status: 'snoozed',
          snoozed_until: until.toISOString(),
          snoozed_by: user.id,
        }, { onConflict: 'rule_key,entity_type,entity_id' });
      if (error) throw error;
      await fetchItemStatuses();
      return { success: true };
    } catch (err) {
      console.error('Failed to snooze radar item:', err);
      return { success: false, error: err.message };
    }
  }, [fetchItemStatuses]);

  // Resolve a radar item
  const resolveItem = useCallback(async (ruleKey, entityType, entityId) => {
    try {
      const { user } = await requireSession();
      const { error } = await supabase
        .from('radar_item_status')
        .upsert({
          rule_key: ruleKey,
          entity_type: entityType,
          entity_id: String(entityId),
          status: 'resolved',
          resolved_at: new Date().toISOString(),
          resolved_by: user.id,
        }, { onConflict: 'rule_key,entity_type,entity_id' });
      if (error) throw error;
      await fetchItemStatuses();
      return { success: true };
    } catch (err) {
      console.error('Failed to resolve radar item:', err);
      return { success: false, error: err.message };
    }
  }, [fetchItemStatuses]);

  // Dismiss a radar item permanently (e.g. old irrelevant warnings)
  const dismissItem = useCallback(async (ruleKey, entityType, entityId) => {
    try {
      const { user } = await requireSession();
      const { error } = await supabase
        .from('radar_item_status')
        .upsert({
          rule_key: ruleKey,
          entity_type: entityType,
          entity_id: String(entityId),
          status: 'dismissed',
          dismissed_at: new Date().toISOString(),
          dismissed_by: user.id,
        }, { onConflict: 'rule_key,entity_type,entity_id' });
      if (error) throw error;
      await fetchItemStatuses();
      return { success: true };
    } catch (err) {
      console.error('Failed to dismiss radar item:', err);
      return { success: false, error: err.message };
    }
  }, [fetchItemStatuses]);

  // Assign an owner to a radar item
  const assignOwner = useCallback(async (ruleKey, entityType, entityId, userId) => {
    try {
      await requireSession();
      const { error } = await supabase
        .from('radar_item_status')
        .upsert({
          rule_key: ruleKey,
          entity_type: entityType,
          entity_id: String(entityId),
          status: 'open',
          assigned_to: userId || null,
        }, { onConflict: 'rule_key,entity_type,entity_id' });
      if (error) throw error;
      await fetchItemStatuses();
      return { success: true };
    } catch (err) {
      console.error('Failed to assign radar item owner:', err);
      return { success: false, error: err.message };
    }
  }, [fetchItemStatuses]);

  // Bulk action: apply status change to multiple items at once
  const bulkAction = useCallback(async (items, action, snoozeDays = 7) => {
    try {
      const { user } = await requireSession();
      const now = new Date().toISOString();
      const rows = items.map(item => {
        const base = {
          rule_key: item.type,
          entity_type: item.entity_type,
          entity_id: String(item.entity_id),
        };
        switch (action) {
          case 'acknowledge':
            return { ...base, status: 'acknowledged', acknowledged_at: now, acknowledged_by: user.id };
          case 'snooze': {
            const until = new Date();
            until.setDate(until.getDate() + snoozeDays);
            return { ...base, status: 'snoozed', snoozed_until: until.toISOString(), snoozed_by: user.id };
          }
          case 'resolve':
            return { ...base, status: 'resolved', resolved_at: now, resolved_by: user.id };
          case 'dismiss':
            return { ...base, status: 'dismissed', dismissed_at: now, dismissed_by: user.id };
          default:
            return base;
        }
      });

      const { error } = await supabase
        .from('radar_item_status')
        .upsert(rows, { onConflict: 'rule_key,entity_type,entity_id' });
      if (error) throw error;
      await fetchItemStatuses();
      return { success: true };
    } catch (err) {
      console.error('Failed to bulk action radar items:', err);
      return { success: false, error: err.message };
    }
  }, [fetchItemStatuses]);

  return {
    settings,
    itemStatuses,
    loading,
    getRuleConfig,
    isRuleEnabled,
    updateRule,
    getItemStatus,
    acknowledgeItem,
    snoozeItem,
    resolveItem,
    dismissItem,
    assignOwner,
    bulkAction,
    refresh: () => Promise.all([fetchSettings(), fetchItemStatuses()]),
  };
}
